import React, { useState } from 'react';
import { Button, Chip, EmptyState, ListItem, PageHeader, StatusDot } from '@origo/origo-ui';
import { PlusIcon, LinkIcon, CaretRightIcon, ClockIcon } from '@phosphor-icons/react';
import { useBindings, useSchedulesForBindings } from '../../api/hooks';
import { PanelError, PanelLoading } from '../common/PanelStates';
import { BindingForm } from './BindingForm';
import { BindingDetail } from './BindingDetail';
import { EditMappingFlow } from './EditMappingFlow';
import { SyncTrigger } from './SyncTrigger';
import { CreateBindingFlow } from './create/CreateBindingFlow';
import type { SyncBinding } from '../../api/types';
import styles from '../common/panel.module.css';

interface BindingsPanelProps {
    defaultSystem: string;
}

export const BindingsPanel: React.FC<BindingsPanelProps> = ({ defaultSystem }) => {
    const { data: bindings, isLoading, isError, error, refetch } = useBindings(false);
    const scheduleOverview = useSchedulesForBindings(bindings);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [editOpen, setEditOpen] = useState(false);
    const [editing, setEditing] = useState<SyncBinding | null>(null);
    const [mappingOpen, setMappingOpen] = useState(false);
    const [mappingBinding, setMappingBinding] = useState<SyncBinding | null>(null);
    const [creating, setCreating] = useState(false);

    const selected = bindings?.find((b) => b.bindingId === selectedId) ?? null;

    // Detalj-visning
    if (selected) {
        return (
            <>
                <BindingDetail
                    binding={selected}
                    onBack={() => setSelectedId(null)}
                    onEdit={() => {
                        setEditing(selected);
                        setEditOpen(true);
                    }}
                    onEditMapping={() => {
                        setMappingBinding(selected);
                        setMappingOpen(true);
                    }}
                    onDeleted={() => setSelectedId(null)}
                />
                <BindingForm
                    open={editOpen}
                    onClose={() => setEditOpen(false)}
                    existing={editing}
                    defaultSystem={defaultSystem}
                />
                <EditMappingFlow
                    open={mappingOpen}
                    binding={mappingBinding}
                    onClose={() => setMappingOpen(false)}
                    onSaved={() => setMappingOpen(false)}
                />
            </>
        );
    }

    // Liste-visning
    return (
        <>
            <PageHeader
                title="Koblinger"
                description="Synkroniseringskoblinger mellom AGOL-tjenester og forvaltningssystemet."
                actions={
                    <Button
                        variant="primary"
                        size="sm"
                        leftIcon={<PlusIcon size={16} weight="bold" />}
                        onClick={() => setCreating(true)}
                    >
                        Opprett kobling
                    </Button>
                }
            />

            <div className={styles.scroll}>
                {isLoading && <PanelLoading label="Laster koblinger…" />}
                {isError && <PanelError error={error} onRetry={() => refetch()} />}

                {!isLoading && !isError && bindings && bindings.length === 0 && (
                    <EmptyState
                        icon={<LinkIcon size={32} weight="duotone" />}
                        title="Ingen koblinger ennå"
                        description="Opprett en kobling for å synkronisere et datasett mellom AGOL og forvaltningssystemet."
                        action={
                            <Button
                                variant="primary"
                                size="sm"
                                leftIcon={<PlusIcon size={16} weight="bold" />}
                                onClick={() => setCreating(true)}
                            >
                                Opprett kobling
                            </Button>
                        }
                    />
                )}

                {!isLoading && !isError && bindings && bindings.length > 0 && (
                    <>
                        <div className={styles.list}>
                            {bindings.map((b) => {
                                const hasSchedule = (scheduleOverview.byBinding.get(b.bindingId) ?? []).some(
                                    (s) => s.enabled,
                                );
                                return (
                                    <ListItem
                                        key={b.bindingId}
                                        onClick={() => setSelectedId(b.bindingId)}
                                        leading={<StatusDot variant={b.enabled ? 'success' : 'warning'} label={b.enabled ? 'Aktivert' : 'Deaktivert'} />}
                                        trailing={<CaretRightIcon size={16} />}
                                    >
                                        <div className={styles.itemBody}>
                                            <div className={styles.itemName}>{b.displayName}</div>
                                            <div className={styles.itemMeta}>
                                                <span className={styles.itemSystem}>{b.forvaltningssystem}</span>
                                                {!b.mappingProfileJson && (
                                                    <Chip variant="warning">mangler mapping</Chip>
                                                )}
                                                {hasSchedule && (
                                                    <Chip variant="info" icon={<ClockIcon size={12} weight="bold" />}>
                                                        tidsstyrt
                                                    </Chip>
                                                )}
                                            </div>
                                            <div className={styles.itemSubtitle}>{b.bindingId}</div>
                                        </div>
                                    </ListItem>
                                );
                            })}
                        </div>

                        <div className={styles.section}>
                            <h3 className={styles.sectionTitle}>Synk alle koblinger</h3>
                            <SyncTrigger contextLabel="alle aktiverte" />
                        </div>
                    </>
                )}
            </div>

            <CreateBindingFlow
                open={creating}
                defaultSystem={defaultSystem}
                onClose={() => setCreating(false)}
                onCreated={(b) => {
                    setCreating(false);
                    setSelectedId(b.bindingId);
                }}
            />
        </>
    );
};
